import React from "react";
import { Edit, GraduationCap, LayoutGrid, List, MessageCircle, Phone, Sparkles, X, Heart } from "lucide-react";
import { ICONS as GLOBAL_ICONS } from "../../utils/icons";
import CounselingPortalTooltip from "./CounselingPortalTooltip";

export default function CounselingTimelineHeader({
  selectedStudent,
  isMobile,
  theme,
  targetGroup,
  studentLogs,
  setViewingStudent,
  setViewMode,
  viewMode,
  setIsCreatingLog,
  isCreatingLog,
}) {
  if (!selectedStudent) return null;
  const accentColor = theme?.primaryHex || "#354F38";

  return (
    <div className={`p-6 pb-6 bg-white border-b border-gray-100 relative z-20 ${isMobile ? "rounded-none pt-4" : "rounded-t-[3rem]"}`}>
      <div className={`flex ${isMobile ? "flex-col gap-6" : "justify-between items-center"} relative z-10`}>
        <div className="flex-1 flex gap-6">
          <div onClick={() => setViewingStudent(selectedStudent)} className="relative group cursor-pointer flex-shrink-0">
            {/* Premium Photo Frame */}
            <div className={`${isMobile ? "w-20 h-20 rounded-2xl" : "w-28 h-28 rounded-[2.5rem]"} bg-white shadow-[0_8px_30px_rgb(0,0,0,0.06)] flex items-center justify-center border-4 border-white transition-all duration-500 z-10 relative group-hover:scale-110 overflow-hidden`}>
              {selectedStudent.photoUrl ? (
                <img src={selectedStudent.photoUrl} className="w-full h-full object-cover" alt="profile" />
              ) : (
                <img
                  src={
                    targetGroup === "teachers"
                      ? selectedStudent.gender === "여"
                        ? GLOBAL_ICONS.teacher_female_animated
                        : GLOBAL_ICONS.teacher_male_animated
                      : selectedStudent.gender === "남"
                        ? "/images/boy_animated_light.png"
                        : "/images/girl_animated_light.png"
                  }
                  className="w-full h-full object-cover transform scale-110"
                  alt="profile"
                />
              )}
            </div>
            {/* Badge removed per user request */}
          </div>

          <div className="flex flex-col gap-2 flex-1 min-w-0 mr-4">
            <div className="flex items-center gap-3">
              <h2
                onClick={() => setViewingStudent(selectedStudent)}
                className={`${isMobile ? "text-3xl" : "text-4xl"} font-black text-gray-900 font-do-hyeon cursor-pointer hover:underline decoration-wavy decoration-emerald-200 underline-offset-8 transition-all`}
              >
                {selectedStudent.name}
              </h2>

              <div className="flex items-center gap-1.5 opacity-90 mt-2">
                {studentLogs.slice(0, 5).map((log, i) => {
                  let iconSrc = "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Smilies/Neutral%20Face.png";
                  let sentimentKr = "평온";

                  if (log.sentiment === "happy") {
                    iconSrc = "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Smilies/Grinning%20Face%20with%20Smiling%20Eyes.png";
                    sentimentKr = "기쁨";
                  }
                  if (log.sentiment === "sad" || log.sentiment === "bad") {
                    iconSrc = "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Smilies/Crying%20Face.png";
                    sentimentKr = "슬픔";
                  }

                  const dateStr = log.date.slice(5).replace("-", ".");

                  return (
                    <CounselingPortalTooltip key={i} content={`${dateStr} ${sentimentKr}`} theme={theme}>
                      <div className="transition-all hover:scale-125 cursor-help relative group filter hover:drop-shadow-md">
                        <img src={iconSrc} alt={log.sentiment} className="w-5 h-5 object-contain" />
                      </div>
                    </CounselingPortalTooltip>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
              <span className="bg-gray-50 px-3 py-1 rounded-full border border-gray-100 flex items-center gap-2 text-[12px] text-gray-500 font-black whitespace-nowrap">
                <GraduationCap size={13} className="text-gray-400" />
                {selectedStudent.school || "학교 미입력"}
              </span>
              {selectedStudent.prayerTopic && (
                <div className="bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100 text-[12px] font-black text-emerald-700 inline-flex items-center gap-2 whitespace-nowrap max-w-full truncate shadow-sm">
                  <Sparkles size={12} className="text-emerald-500 animate-pulse" />
                  <span className="truncate max-w-[180px]">{selectedStudent.prayerTopic}</span>
                </div>
              )}
            </div>

            {selectedStudent.phone && (
              <div className="flex items-center gap-2 mt-2">
                <a
                  href={`tel:${selectedStudent.phone}`}
                  className={`${isMobile ? "px-4 py-2 text-[12px]" : "px-6 py-2.5 text-sm"} text-white rounded-2xl shadow-lg ring-4 ring-black/5 font-black flex items-center justify-center gap-2 hover:brightness-110 hover:scale-[1.05] transition-all`}
                  style={{ backgroundColor: accentColor }}
                >
                  <Phone size={14} fill="currentColor" /> 전화
                </a>
                <a href={`sms:${selectedStudent.phone}`} className={`${isMobile ? "px-4 py-2 text-[12px]" : "px-6 py-2.5 text-sm"} bg-[#FFEAA1] text-[#4A3B10] border border-[#F0D060] rounded-2xl shadow-md font-black flex items-center justify-center gap-2 hover:bg-[#FFD54F] hover:scale-[1.05] transition-all`}>
                  <MessageCircle size={14} /> 문자
                </a>
              </div>
            )}
          </div>
        </div>

        <div className={`flex ${isMobile ? "w-full flex-row justify-between items-center bg-gray-50 p-3 rounded-2xl border border-gray-100" : "flex-col items-end gap-3"}`}>
          <div className="flex items-center gap-2">
            <div className="text-right mr-3 flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-gray-100 shadow-sm">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Logs</span>
              <span className="text-emerald-600 text-base font-black">{studentLogs.length}</span>
            </div>
            <div className="flex bg-gray-100/60 p-1.5 rounded-2xl border border-gray-200/50">
              <button
                onClick={() => setViewMode("timeline")}
                className={`relative group p-2 rounded-xl transition-all duration-300 ${viewMode === "timeline" ? `bg-white shadow-md text-gray-800` : "text-gray-400 hover:text-gray-600"}`}
              >
                <List size={20} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`relative group p-2 rounded-xl transition-all duration-300 ${viewMode === "list" ? `bg-white shadow-md text-gray-800` : "text-gray-400 hover:text-gray-600"}`}
              >
                <LayoutGrid size={20} />
              </button>
            </div>
          </div>

          <button
            onClick={() => setIsCreatingLog(!isCreatingLog)}
            className={`${isMobile ? "px-5 py-2.5" : "w-full py-4 px-6"} rounded-[1.5rem] transition-all duration-500 flex items-center justify-center gap-2 shadow-lg active:scale-95 ${isCreatingLog ? "bg-rose-50 text-rose-500 border border-rose-100" : "text-white hover:brightness-110 ring-4 ring-black/5"}`}
            style={!isCreatingLog ? { backgroundColor: accentColor } : {}}
            title="기록 작성"
          >
            {isCreatingLog ? <X size={18} /> : <Edit size={18} />}
            <span className="text-base font-black tracking-tight">{isCreatingLog ? "닫기" : "새 기록 작성"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
